/**
 * Fetch action challenge + sign with connected Solana wallet.
 * Required for table join, slots spin, cashout, pro claim (edge play ledger).
 */
import {
  signMessageBase58,
  type ConnectedSolanaWallet,
} from "@/lib/solana-wallets";

export type PlayAction =
  | "play_session"
  | "table_join"
  | "slots_spin"
  | "cashout"
  | "pro_claim";

export type SignedChallenge = {
  message: string;
  signature: string;
  nonce: string;
  action: PlayAction;
};

function walletHeaders(wallet: string): Headers {
  const h = new Headers({ Accept: "application/json" });
  h.set("X-Wallet-Address", wallet);
  try {
    const token =
      localStorage.getItem("grudge_auth_token") ||
      localStorage.getItem("grudge_session_token");
    if (token) h.set("Authorization", `Bearer ${token}`);
  } catch {
    /* ignore */
  }
  return h;
}

/**
 * GET /api/wallet/challenge?action=… then Phantom/injected signMessage.
 */
export async function signPlayAction(opts: {
  wallet: ConnectedSolanaWallet;
  action: PlayAction;
  params?: Record<string, string | number | undefined>;
}): Promise<SignedChallenge> {
  const pk = opts.wallet.publicKey;
  if (!pk || pk.length < 32) throw new Error("Wallet not connected");

  const q = new URLSearchParams({
    action: opts.action,
    wallet: pk,
  });
  if (opts.params) {
    for (const [k, v] of Object.entries(opts.params)) {
      if (v === undefined || v === null || v === "") continue;
      q.set(k, String(v));
    }
  }

  const r = await fetch(`/api/wallet/challenge?${q}`, {
    headers: walletHeaders(pk),
  });
  const j = (await r.json()) as {
    success?: boolean;
    message?: string;
    nonce?: string;
    action?: string;
    error?: string;
  };
  if (!r.ok || !j.message) {
    throw new Error(j.error || "Could not build wallet challenge");
  }

  const signature = await signMessageBase58(opts.wallet, j.message);
  return {
    message: j.message,
    signature,
    nonce: j.nonce || "",
    action: opts.action,
  };
}

/** Body fields to merge into money-moving POSTs. */
export function signedBodyFields(signed: SignedChallenge): {
  message: string;
  signature: string;
  challengeMessage: string;
  challengeSignature: string;
} {
  return {
    message: signed.message,
    signature: signed.signature,
    challengeMessage: signed.message,
    challengeSignature: signed.signature,
  };
}
